import { MoodDuration } from '../consts/mood-audio';

export type SceneRoom = 'livingspace_a' | 'hallway_a' | 'bedroom_a';
export type SceneClimateDevice = 'livingspace_a' | 'bedroom_a';
export type ScenePresetRef = { preset: 1 | 2 | 3 | 4 };

export type SceneLightAction = {
  type: 'light'; room: SceneRoom; state: 'on' | 'off';
  brightness?: number; color?: string | ScenePresetRef;
};
export type SceneClimateAction = {
  type: 'climate'; device: SceneClimateDevice;
  mode?: 'off' | 'cool' | 'heat'; temp?: number | 'wake' | 'sleep';
};
export type SceneAudioAction = {
  type: 'audio';
  state: 'on' | 'off';
  /** MoodSound id (file stem) — required when state === 'on'. */
  sound?: string;
  /** Minutes. Required when state === 'on'. There is no indefinite option. */
  duration?: MoodDuration;
  /**
   * The 1-100 level to play this scene's audio at, captured when the scene is
   * saved. The editor always writes one, defaulting to the level its slider
   * shows, so what a scene plays at is fixed at save time rather than inherited
   * from whatever the transport bar was last left at.
   *
   * Firing the scene adopts this as the resident's own level, exactly as dragging
   * the slider would — it does not revert when the session ends.
   *
   * Optional only for scenes saved BEFORE this existed. The unit falls back to
   * the resident's remembered level for those, which is the behaviour they were
   * saved under; re-saving such a scene stamps in whatever the slider shows.
   */
  volume?: number;
};
export type SceneAction = SceneLightAction | SceneClimateAction | SceneAudioAction;

export interface SceneDef {
  id: string;
  name: string;
  icon: string;          // ionicon name
  color: string;         // accent hex e.g. '#FFB400'
  isDefault: boolean;
  order: number;
  actions: SceneAction[];
}

/** Stored in ResidentSettings under SCENES_KEY. Defaults come from code; only overrides+custom persist. */
export interface ScenesBlob {
  version: number;
  overrides: { [defaultId: string]: SceneDef };
  custom: SceneDef[];
  /** Global display order of scene ids (defaults + custom). Scenes not listed append at the end. */
  order?: string[];
  /** Monotonic save counter. The only key used to reconcile against the unit's
   *  copy — never timestamps, which are subject to device clock skew.
   *  Absent on blobs written before unit sync; treat as 0. */
  rev?: number;
}

export const SCENES_KEY = 'scenes';
export const SCENES_BLOB_VERSION = 1;
export const MAX_CUSTOM_SCENES = 30;

export const DEFAULT_SCENES: SceneDef[] = [
  { id: 'default.good_morning', name: 'Good Morning', icon: 'sunny-outline', color: '#FFB400', isDefault: true, order: 0, actions: [
    // Colour is explicit rather than omitted: an action with no colour makes the engine skip the
    // colour step entirely, so the light came up in whatever colour it was last left in, and the
    // editor showed no swatch selected. Preset 4 is the white/circadian swatch.
    { type: 'light', room: 'bedroom_a', state: 'on', brightness: 100, color: { preset: 4 } },
    { type: 'climate', device: 'livingspace_a', temp: 'wake' },
    { type: 'climate', device: 'bedroom_a', temp: 'wake' },
  ]},
  { id: 'default.good_night', name: 'Good Night', icon: 'moon-outline', color: '#5AB0FF', isDefault: true, order: 1, actions: [
    { type: 'light', room: 'livingspace_a', state: 'off' },
    { type: 'climate', device: 'livingspace_a', temp: 'sleep' },
    { type: 'climate', device: 'bedroom_a', temp: 'sleep' },
  ]},
  { id: 'default.movie_time', name: 'Movie Time', icon: 'film-outline', color: '#8B7CF6', isDefault: true, order: 2, actions: [
    { type: 'light', room: 'hallway_a', state: 'off' },
    { type: 'light', room: 'bedroom_a', state: 'off' },
    { type: 'light', room: 'livingspace_a', state: 'on', brightness: 10 },
  ]},
  { id: 'default.party_mode', name: 'Party Mode', icon: 'sparkles-outline', color: '#FF6B8A', isDefault: true, order: 3, actions: [
    { type: 'light', room: 'bedroom_a', state: 'on', color: { preset: 1 } },
    { type: 'light', room: 'hallway_a', state: 'on', color: { preset: 2 } },
    { type: 'light', room: 'livingspace_a', state: 'on', color: { preset: 3 } },
  ]},
];

export function buildScenePayload(def: SceneDef): { actions: SceneAction[] } {
  return { actions: def.actions };
}
